// Medical Scores Database - Extended Pediatric, ORL, and Sport Medicine Scores
// Comprehensive pediatric, ENT, and physical medicine assessment tools

import {
  Baby,
  Stethoscope,
  Activity,
  Wind,
  Thermometer,
  AlertTriangle
} from 'lucide-react';

// ==================== PÉDIATRIE ÉTENDUE ====================
export const PEDIATRIC_EXTRA_SCORES = {
  apgarNeonatal: {
    name: 'Score d\'Apgar',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'État du nouveau-né',
    color: 'violet',
    fields: [
      { id: 'appearance', label: 'Apparence (coloration)', type: 'select', options: [
        { value: 0, label: '0 - Cyanose ou pâleur généralisée' },
        { value: 1, label: '1 - Acrocyanose (corps rose, extrémités bleues)' },
        { value: 2, label: '2 - Entièrement rose' }
      ]},
      { id: 'pulse', label: 'Pouls', type: 'select', options: [
        { value: 0, label: '0 - Absent' },
        { value: 1, label: '1 - < 100/min' },
        { value: 2, label: '2 - ≥ 100/min' }
      ]},
      { id: 'grimace', label: 'Grimace (réactivité)', type: 'select', options: [
        { value: 0, label: '0 - Pas de réponse' },
        { value: 1, label: '1 - Grimace' },
        { value: 2, label: '2 - Cri, toux, éternuement' }
      ]},
      { id: 'activity', label: 'Activité (tonus)', type: 'select', options: [
        { value: 0, label: '0 - Flasque' },
        { value: 1, label: '1 - Flexion légère' },
        { value: 2, label: '2 - Mouvements actifs' }
      ]},
      { id: 'respiration', label: 'Respiration', type: 'select', options: [
        { value: 0, label: '0 - Absente' },
        { value: 1, label: '1 - Lente, irrégulière' },
        { value: 2, label: '2 - Cri vigoureux' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score >= 7) return { level: 'success', text: `Apgar ${score}/10`, detail: 'Bonne adaptation à la vie extra-utérine' };
      if (score >= 4) return { level: 'warning', text: `Apgar ${score}/10`, detail: 'Détresse modérée. Stimulation, oxygénation' };
      return { level: 'danger', text: `Apgar ${score}/10`, detail: 'Détresse sévère. Réanimation néonatale' };
    },
    formula: 'Apgar: Apparence, Pouls, Grimace, Activité, Respiration'
  },

  silverman: {
    name: 'Score de Silverman',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Détresse respiratoire du nouveau-né',
    color: 'violet',
    fields: [
      { id: 'thorax', label: 'Tirage intercostal', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Modéré' }, { value: 2, label: '2 - Intense et constant' }
      ]},
      { id: 'xiphoid', label: 'Tirage xiphoïdien', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Modéré' }, { value: 2, label: '2 - Intense' }
      ]},
      { id: 'seesaw', label: 'Balancement thoraco-abdominal', type: 'select', options: [
        { value: 0, label: '0 - Respiration synchrone' }, { value: 1, label: '1 - Thorax immobile' }, { value: 2, label: '2 - Respiration paradoxale' }
      ]},
      { id: 'flaring', label: 'Battement des ailes du nez', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Modéré' }, { value: 2, label: '2 - Intense' }
      ]},
      { id: 'grunting', label: 'Geignement expiratoire', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - Audible au stéthoscope' }, { value: 2, label: '2 - Audible à distance' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `Silverman ${score}/10`, detail: 'Détresse légère. Surveillance' };
      if (score <= 5) return { level: 'warning', text: `Silverman ${score}/10`, detail: 'Détresse modérée. O₂, CPAP' };
      return { level: 'danger', text: `Silverman ${score}/10`, detail: 'Détresse sévère. Intubation à discuter' };
    },
    formula: 'Silverman: 5 signes de lutte respiratoire (0-2 chacun)'
  },

  wangBronchiolite: {
    name: 'Score de Wang',
    category: 'Pédiatrie',
    icon: Wind,
    description: 'Sévérité bronchiolite',
    color: 'violet',
    fields: [
      { id: 'rr', label: 'Fréquence respiratoire', type: 'select', options: [
        { value: 0, label: '0 - < 30/min' }, { value: 1, label: '1 - 31-45/min' },
        { value: 2, label: '2 - 46-60/min' }, { value: 3, label: '3 - > 60/min' }
      ]},
      { id: 'wheezing', label: 'Sibilants', type: 'select', options: [
        { value: 0, label: '0 - Aucun' }, { value: 1, label: '1 - Fin expiration (stétho)' },
        { value: 2, label: '2 - Toute expiration (stétho)' }, { value: 3, label: '3 - Audibles sans stétho' }
      ]},
      { id: 'retractions', label: 'Tirage', type: 'select', options: [
        { value: 0, label: '0 - Aucun' }, { value: 1, label: '1 - Intercostal seul' },
        { value: 2, label: '2 - Intercostal + sous-costal' }, { value: 3, label: '3 - + sus-sternal' }
      ]},
      { id: 'generalCondition', label: 'État général', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Irritable, difficultés alimentation' },
        { value: 2, label: '2 - Léthargie légère' }, { value: 3, label: '3 - Léthargie sévère' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 12 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `Wang ${score}/12`, detail: 'Bronchiolite légère. Ambulatoire' };
      if (score <= 8) return { level: 'warning', text: `Wang ${score}/12`, detail: 'Bronchiolite modérée. Hospitalisation à discuter' };
      return { level: 'danger', text: `Wang ${score}/12`, detail: 'Bronchiolite sévère. Hospitalisation, O₂' };
    },
    formula: 'Wang = FR + Sibilants + Tirage + État général'
  },

  woodAsthme: {
    name: 'Score de Wood-Downes',
    category: 'Pédiatrie',
    icon: Wind,
    description: 'Sévérité crise d\'asthme enfant',
    color: 'violet',
    fields: [
      { id: 'cyanosis', label: 'Cyanose', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - En air ambiant' }
      ]},
      { id: 'breathSounds', label: 'Murmure vésiculaire', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Diminué' }, { value: 2, label: '2 - Très diminué ou absent' }
      ]},
      { id: 'accessory', label: 'Muscles accessoires', type: 'select', options: [
        { value: 0, label: '0 - Pas utilisés' }, { value: 1, label: '1 - Modérément utilisés' }, { value: 2, label: '2 - Activité maximale' }
      ]},
      { id: 'wheezing', label: 'Sibilants', type: 'select', options: [
        { value: 0, label: '0 - Absents' }, { value: 1, label: '1 - Modérés' }, { value: 2, label: '2 - Marqués' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 0, label: '0 - Normale' }, { value: 1, label: '1 - Agitation ou léthargie' }, { value: 2, label: '2 - Coma' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 9 };
    },
    interpret: (score) => {
      if (score <= 3) return { level: 'success', text: `Wood ${score}/9`, detail: 'Crise légère. β2 inhalés' };
      if (score <= 5) return { level: 'warning', text: `Wood ${score}/9`, detail: 'Crise modérée. β2 + corticoïdes' };
      return { level: 'danger', text: `Wood ${score}/9`, detail: 'Crise sévère. Nébulisation continue, Mg IV, réa' };
    },
    formula: 'Wood-Downes: cyanose, MV, muscles accessoires, sibilants, conscience'
  },

  guarino: {
    name: 'Score de Guarino',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Déshydratation sur diarrhée aiguë',
    color: 'violet',
    fields: [
      { id: 'generalAppearance', label: 'Aspect général', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - Soif, agitation' }, { value: 2, label: '2 - Léthargique ou inconscient' }
      ]},
      { id: 'eyes', label: 'Yeux', type: 'select', options: [
        { value: 0, label: '0 - Normaux' }, { value: 1, label: '1 - Légèrement enfoncés' }, { value: 2, label: '2 - Très enfoncés' }
      ]},
      { id: 'tears', label: 'Larmes', type: 'select', options: [
        { value: 0, label: '0 - Présentes' }, { value: 1, label: '1 - Diminuées' }, { value: 2, label: '2 - Absentes' }
      ]},
      { id: 'mucous', label: 'Muqueuses', type: 'select', options: [
        { value: 0, label: '0 - Humides' }, { value: 1, label: '1 - Sèches' }, { value: 2, label: '2 - Très sèches' }
      ]},
      { id: 'skinPinch', label: 'Pli cutané', type: 'select', options: [
        { value: 0, label: '0 - Normal' }, { value: 1, label: '1 - S\'efface lentement' }, { value: 2, label: '2 - S\'efface très lentement (> 2s)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Guarino ${score}/10`, detail: 'Pas de déshydratation ou légère (< 3%). SRO per os' };
      if (score <= 6) return { level: 'warning', text: `Guarino ${score}/10`, detail: 'Déshydratation modérée (3-9%). SRO surveillance' };
      return { level: 'danger', text: `Guarino ${score}/10`, detail: 'Déshydratation sévère (> 9%). Réhydratation IV' };
    },
    formula: 'Guarino/CDS: 5 signes cliniques de déshydratation'
  },

  blantyre: {
    name: 'Score de Blantyre',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Coma palustre chez l\'enfant',
    color: 'violet',
    fields: [
      { id: 'eye', label: 'Mouvement oculaire', type: 'select', options: [
        { value: 0, label: '0 - Pas de mouvement dirigé' },
        { value: 1, label: '1 - Suit ou dirige les yeux' }
      ]},
      { id: 'verbal', label: 'Réponse verbale', type: 'select', options: [
        { value: 0, label: '0 - Pas de réponse' },
        { value: 1, label: '1 - Gémissement ou cri inapproprié' },
        { value: 2, label: '2 - Cri approprié' }
      ]},
      { id: 'motor', label: 'Réponse motrice', type: 'select', options: [
        { value: 0, label: '0 - Pas de réponse ou extension' },
        { value: 1, label: '1 - Retrait à la douleur' },
        { value: 2, label: '2 - Localise la douleur' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score >= 4) return { level: 'success', text: `Blantyre ${score}/5`, detail: 'Pas de coma ou altération légère' };
      if (score >= 2) return { level: 'warning', text: `Blantyre ${score}/5`, detail: 'Coma modéré' };
      return { level: 'danger', text: `Blantyre ${score}/5`, detail: 'Coma profond. Paludisme grave' };
    },
    formula: 'Blantyre ≤ 2 = coma palustre (équivalent Glasgow pédiatrique)'
  },

  tailleCible: {
    name: 'Taille cible génétique',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Estimation taille adulte',
    color: 'violet',
    fields: [
      { id: 'fatherHeight', label: 'Taille père (cm)', type: 'number', min: 150, max: 210 },
      { id: 'motherHeight', label: 'Taille mère (cm)', type: 'number', min: 140, max: 195 },
      { id: 'sex', label: 'Sexe de l\'enfant', type: 'select', options: [
        { value: 'M', label: 'Garçon' }, { value: 'F', label: 'Fille' }
      ]}
    ],
    calculate: (values) => {
      const father = values.fatherHeight || 175;
      const mother = values.motherHeight || 163;
      const isMale = values.sex === 'M';
      // Formule de Tanner
      const targetHeight = isMale ? 
        (father + mother + 13) / 2 :
        (father + mother - 13) / 2;
      return { score: targetHeight.toFixed(1), max: 'cm', details: { min: (targetHeight - 8.5).toFixed(1), max: (targetHeight + 8.5).toFixed(1) } };
    },
    interpret: (score, result) => {
      const min = result?.details?.min || (parseFloat(score) - 8.5);
      const max = result?.details?.max || (parseFloat(score) + 8.5);
      return { level: 'success', text: `Taille cible: ${score} cm`, detail: `Intervalle attendu: ${min} - ${max} cm (± 8.5 cm)` };
    },
    formula: 'Garçon: (Père + Mère + 13) / 2 | Fille: (Père + Mère - 13) / 2'
  },

  ageCorrige: {
    name: 'Âge corrigé du prématuré',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Calcul âge corrigé',
    color: 'violet',
    fields: [
      { id: 'chronologicalWeeks', label: 'Âge chronologique (semaines)', type: 'number', min: 0, max: 156 },
      { id: 'gestationalAge', label: 'Âge gestationnel à la naissance (SA)', type: 'number', min: 24, max: 42 }
    ],
    calculate: (values) => {
      const chrono = values.chronologicalWeeks || 20;
      const ga = values.gestationalAge || 32;
      const prematurity = 40 - ga;
      const correctedWeeks = chrono - prematurity;
      const correctedMonths = (correctedWeeks / 4.33).toFixed(1);
      return { score: correctedWeeks, max: 'semaines', details: { months: correctedMonths, prematurity } };
    },
    interpret: (score, result) => {
      const months = result?.details?.months || 0;
      const prema = result?.details?.prematurity || 0;
      return { level: 'success', text: `Âge corrigé: ${score} semaines`, detail: `Soit ~${months} mois. Prématurité: ${prema} semaines. Utiliser jusqu'à 2 ans` };
    },
    formula: 'Âge corrigé = Âge chronologique - (40 - AG naissance)'
  }
};

// ==================== ORL ====================
export const ORL_SCORES = {
  stopBang: {
    name: 'STOP-BANG',
    category: 'ORL',
    icon: Stethoscope,
    description: 'Dépistage syndrome d\'apnée du sommeil',
    color: 'rose',
    fields: [
      { id: 'snoring', label: 'S - Ronflement fort', type: 'checkbox', points: 1 },
      { id: 'tired', label: 'T - Fatigue diurne', type: 'checkbox', points: 1 },
      { id: 'observed', label: 'O - Apnées observées', type: 'checkbox', points: 1 },
      { id: 'pressure', label: 'P - HTA traitée', type: 'checkbox', points: 1 },
      { id: 'bmi', label: 'B - IMC > 35 kg/m²', type: 'checkbox', points: 1 },
      { id: 'age', label: 'A - Âge > 50 ans', type: 'checkbox', points: 1 },
      { id: 'neck', label: 'N - Tour de cou > 40 cm', type: 'checkbox', points: 1 },
      { id: 'gender', label: 'G - Sexe masculin', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `STOP-BANG ${score}/8`, detail: 'Risque faible de SAOS' };
      if (score <= 4) return { level: 'warning', text: `STOP-BANG ${score}/8`, detail: 'Risque intermédiaire. Polysomnographie à discuter' };
      return { level: 'danger', text: `STOP-BANG ${score}/8`, detail: 'Risque élevé de SAOS. Polysomnographie recommandée' };
    },
    formula: 'STOP-BANG ≥ 5 = haute probabilité SAOS modéré-sévère'
  },

  berlinSAS: {
    name: 'Questionnaire de Berlin',
    category: 'ORL',
    icon: Stethoscope,
    description: 'Dépistage apnées du sommeil',
    color: 'rose',
    fields: [
      { id: 'cat1_snore', label: 'Cat 1: Ronflement (fréquent/quotidien)', type: 'checkbox', points: 1 },
      { id: 'cat1_loud', label: 'Cat 1: Ronflement plus fort que la parole', type: 'checkbox', points: 1 },
      { id: 'cat1_apnea', label: 'Cat 1: Apnées observées', type: 'checkbox', points: 1 },
      { id: 'cat2_tired', label: 'Cat 2: Fatigue au réveil fréquente', type: 'checkbox', points: 1 },
      { id: 'cat2_drowsy', label: 'Cat 2: Somnolence diurne', type: 'checkbox', points: 1 },
      { id: 'cat2_driving', label: 'Cat 2: S\'endort en conduisant', type: 'checkbox', points: 1 },
      { id: 'cat3_hta', label: 'Cat 3: HTA connue', type: 'checkbox', points: 1 },
      { id: 'cat3_bmi', label: 'Cat 3: IMC > 30 kg/m²', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      const cat1 = (values.cat1_snore ? 1 : 0) + (values.cat1_loud ? 1 : 0) + (values.cat1_apnea ? 1 : 0);
      const cat2 = (values.cat2_tired ? 1 : 0) + (values.cat2_drowsy ? 1 : 0) + (values.cat2_driving ? 1 : 0);
      const cat3 = (values.cat3_hta ? 1 : 0) + (values.cat3_bmi ? 1 : 0);
      const positiveCategories = (cat1 >= 2 ? 1 : 0) + (cat2 >= 2 ? 1 : 0) + (cat3 >= 1 ? 1 : 0);
      return { score: positiveCategories, max: 3, details: { cat1, cat2, cat3 } };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `Berlin: ${score}/3 catégories positives`, detail: 'Risque faible de SAOS' };
      return { level: 'danger', text: `Berlin: ${score}/3 catégories positives`, detail: 'Risque élevé de SAOS. Polysomnographie recommandée' };
    },
    formula: 'Berlin: ≥ 2 catégories positives = risque élevé'
  },

  westleyLaryngite: {
    name: 'Score de Westley',
    category: 'ORL',
    icon: Thermometer,
    description: 'Gravité laryngite (croup)',
    color: 'rose',
    fields: [
      { id: 'stridor', label: 'Stridor', type: 'select', options: [
        { value: 0, label: '0 - Absent' },
        { value: 1, label: '1 - À l\'agitation' },
        { value: 2, label: '2 - Au repos' }
      ]},
      { id: 'retractions', label: 'Tirage', type: 'select', options: [
        { value: 0, label: '0 - Absent' },
        { value: 1, label: '1 - Léger' },
        { value: 2, label: '2 - Modéré' },
        { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'airEntry', label: 'Entrée d\'air', type: 'select', options: [
        { value: 0, label: '0 - Normale' },
        { value: 1, label: '1 - Diminuée' },
        { value: 2, label: '2 - Très diminuée' }
      ]},
      { id: 'cyanosis', label: 'Cyanose', type: 'select', options: [
        { value: 0, label: '0 - Absente' },
        { value: 4, label: '4 - En agitation' },
        { value: 5, label: '5 - Au repos' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 0, label: '0 - Normale' },
        { value: 5, label: '5 - Altérée' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 17 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Westley ${score}/17`, detail: 'Laryngite légère. Surveillance' };
      if (score <= 7) return { level: 'warning', text: `Westley ${score}/17`, detail: 'Laryngite modérée. Corticoïdes PO ou IM' };
      if (score <= 11) return { level: 'danger', text: `Westley ${score}/17`, detail: 'Laryngite sévère. Adrénaline nébulisée' };
      return { level: 'danger', text: `Westley ${score}/17`, detail: 'Insuffisance respiratoire. Réanimation' };
    },
    formula: 'Westley: stridor, tirage, entrée air, cyanose, conscience'
  },

  houseBrackmann: {
    name: 'House & Brackmann',
    category: 'ORL',
    icon: Stethoscope,
    description: 'Gravité paralysie faciale périphérique',
    color: 'rose',
    fields: [
      { id: 'grade', label: 'Grade', type: 'select', options: [
        { value: 1, label: 'I - Normal' },
        { value: 2, label: 'II - Dysfonction légère (fermeture oeil complète, asymétrie légère)' },
        { value: 3, label: 'III - Dysfonction modérée (fermeture oeil avec effort, asymétrie évidente)' },
        { value: 4, label: 'IV - Dysfonction modérée-sévère (fermeture incomplète, asymétrie défigurante)' },
        { value: 5, label: 'V - Dysfonction sévère (mouvement à peine perceptible)' },
        { value: 6, label: 'VI - Paralysie totale (aucun mouvement)' }
      ]}
    ],
    calculate: (values) => ({ score: values.grade || 1, max: 6 }),
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'HB Grade I', detail: 'Fonction normale' };
      if (score === 2) return { level: 'success', text: 'HB Grade II', detail: 'Bon pronostic de récupération' };
      if (score <= 3) return { level: 'warning', text: 'HB Grade III', detail: 'Pronostic modéré. Protection cornéenne' };
      if (score <= 4) return { level: 'warning', text: 'HB Grade IV', detail: 'Pronostic réservé' };
      return { level: 'danger', text: `HB Grade ${score === 5 ? 'V' : 'VI'}`, detail: 'Pronostic sombre. Rééducation intensive' };
    },
    formula: 'House-Brackmann I-VI: classification paralysie faciale'
  }
};

// ==================== MÉDECINE DU SPORT ====================
export const SPORT_MEDICINE_SCORES = {
  ruffierDickson: {
    name: 'Test de Ruffier-Dickson',
    category: 'Médecine du Sport',
    icon: Activity,
    description: 'Adaptation à l\'effort',
    color: 'emerald',
    fields: [
      { id: 'p0', label: 'P0 - Pouls repos (bpm)', type: 'number', min: 40, max: 120 },
      { id: 'p1', label: 'P1 - Pouls immédiatement après effort (bpm)', type: 'number', min: 80, max: 200 },
      { id: 'p2', label: 'P2 - Pouls 1 min après effort (bpm)', type: 'number', min: 50, max: 160 }
    ],
    calculate: (values) => {
      const p0 = values.p0 || 70;
      const p1 = values.p1 || 140;
      const p2 = values.p2 || 90;
      const ruffier = (p0 + p1 + p2 - 200) / 10;
      return { score: ruffier.toFixed(1), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 0) return { level: 'success', text: `Ruffier ${score}`, detail: 'Excellent. Aptitude sportive très bonne' };
      if (s < 5) return { level: 'success', text: `Ruffier ${score}`, detail: 'Bon. Aptitude sportive correcte' };
      if (s < 10) return { level: 'warning', text: `Ruffier ${score}`, detail: 'Moyen. Reconditionnement conseillé' };
      if (s < 15) return { level: 'warning', text: `Ruffier ${score}`, detail: 'Insuffisant. Avis cardiologique' };
      return { level: 'danger', text: `Ruffier ${score}`, detail: 'Mauvaise adaptation. CI sport intense' };
    },
    formula: 'Ruffier = (P0 + P1 + P2 - 200) / 10'
  },

  vo2maxTheorique: {
    name: 'VO₂max théorique',
    category: 'Médecine du Sport',
    icon: Activity,
    description: 'Estimation capacité aérobie',
    color: 'emerald',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 80 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' }, { value: 'F', label: 'Femme' }
      ]},
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 40, max: 150 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 140, max: 210 },
      { id: 'activity', label: 'Niveau d\'activité', type: 'select', options: [
        { value: 0, label: 'Sédentaire' },
        { value: 1, label: 'Peu actif' },
        { value: 2, label: 'Actif' },
        { value: 3, label: 'Très actif' }
      ]}
    ],
    calculate: (values) => {
      const age = values.age || 40;
      const isMale = values.sex === 'M';
      const weight = values.weight || 70;
      const height = values.height || 170;
      const activity = values.activity || 1;
      
      // Formule simplifiée
      let vo2 = isMale ? 
        60 - 0.55 * age - 0.2 * weight / ((height/100) * (height/100)) + 3 * activity :
        48 - 0.55 * age - 0.2 * weight / ((height/100) * (height/100)) + 3 * activity;
      
      vo2 = Math.max(vo2, 15);
      return { score: vo2.toFixed(1), max: 'mL/kg/min' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s >= 50) return { level: 'success', text: `VO₂max: ${score} mL/kg/min`, detail: 'Excellente capacité aérobie' };
      if (s >= 40) return { level: 'success', text: `VO₂max: ${score} mL/kg/min`, detail: 'Bonne capacité aérobie' };
      if (s >= 30) return { level: 'warning', text: `VO₂max: ${score} mL/kg/min`, detail: 'Capacité moyenne' };
      if (s >= 20) return { level: 'warning', text: `VO₂max: ${score} mL/kg/min`, detail: 'Capacité faible' };
      return { level: 'danger', text: `VO₂max: ${score} mL/kg/min`, detail: 'Capacité très faible' };
    },
    formula: 'VO₂max théorique (estimation sans test d\'effort)'
  },

  march6min: {
    name: 'Test de marche 6 minutes',
    category: 'Médecine du Sport',
    icon: Activity,
    description: 'Évaluation capacité fonctionnelle',
    color: 'emerald',
    fields: [
      { id: 'distance', label: 'Distance parcourue (mètres)', type: 'number', min: 0, max: 900 },
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 140, max: 210 },
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 200 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' }, { value: 'F', label: 'Femme' }
      ]}
    ],
    calculate: (values) => {
      const distance = values.distance || 400;
      const age = values.age || 60;
      const height = values.height || 170;
      const weight = values.weight || 75;
      const isMale = values.sex === 'M';
      
      // Formule d'Enright pour la valeur prédite
      const predicted = isMale ?
        (7.57 * height) - (5.02 * age) - (1.76 * weight) - 309 :
        (2.11 * height) - (2.29 * weight) - (5.78 * age) + 667;
      
      const percentPredicted = (distance / predicted * 100).toFixed(0);
      const lln = predicted - 153; // Lower limit of normal (environ -153m)
      
      return { score: distance, max: 'm', details: { predicted: Math.round(predicted), percentPredicted, lln: Math.round(lln) } };
    },
    interpret: (score, result) => {
      const pct = result?.details?.percentPredicted || 100;
      const lln = result?.details?.lln || 300;
      if (score >= lln) return { level: 'success', text: `TM6: ${score} m`, detail: `${pct}% de la valeur prédite. Normal` };
      if (pct >= 70) return { level: 'warning', text: `TM6: ${score} m`, detail: `${pct}% de la valeur prédite. Modérément diminué` };
      return { level: 'danger', text: `TM6: ${score} m`, detail: `${pct}% de la valeur prédite. Sévèrement diminué` };
    },
    formula: 'TM6: distance de marche à vitesse confortable en 6 minutes'
  }
};

export const PEDIATRIC_ORL_SPORT_SCORES = {
  ...PEDIATRIC_EXTRA_SCORES,
  ...ORL_SCORES,
  ...SPORT_MEDICINE_SCORES
};

export default PEDIATRIC_ORL_SPORT_SCORES;
